import {
  Component, computed,
  ElementRef,
  EventEmitter,
  forwardRef, inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'rxjs';
import { FocusableControlValueAccessor } from '../FocusableControlValueAccessor';
import { debounce } from '../../../../_core/utils/debounce';

@Component({
  selector: 'opac-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
})
export class TextareaComponent implements FocusableControlValueAccessor, OnInit {
  private readonly updateA11yCharactersCount = debounce((length: number) => {
    this.a11yCharactersCount.set(this.maxLength - length);
  }, 500);

  readonly errorId = Math.random().toString(16).substring(2);
  readonly infinity = Infinity;
  readonly a11yCharactersCount = signal<number>(0);
  readonly a11yCharactersKey = computed(() =>
    this.a11yCharactersCount() < 0 ? 'textarea.a11yCharsExceeded' : 'textarea.a11yCharsRemaining'
  );
  readonly a11yCharactersParams = computed(() => ({ count: Math.abs(this.a11yCharactersCount()) }));

  @ViewChild('textareaEl', { static: true }) textareaEl!: ElementRef<HTMLTextAreaElement>;
  /**
   * Determines textarea autocomplete attribute.
   */
  @Input() autocomplete: string = 'off';
  /**
   * Sets autofocus attribute in native input
   */
  @Input() autofocus: boolean = false;
  /**
   * Determines if counter should be visible.
   * It also needs to set max length to work.
   */
  @Input() counter: boolean = false;
  /**
   * Sets textarea disabled state
   */
  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  }
  /**
   * Textarea id for label join.
   * Defaults to random string.
   */
  @Input() id: string = Math.random().toString(16).substring(2);
  /**
   * Determines if textarea is invalid.
   */
  @Input() invalid: boolean = false;
  /**
   * Determines textarea min length attribute.
   */
  @Input() minLength: number = 0;
  /**
   * Determines textarea max length attribute.
   * Needed to show the counter.
   */
  @Input() maxLength: number = this.infinity;
  /**
   * The visible width of the text control, in average character widths.
   */
  @Input() cols: number = 20;
  /**
   * The number of visible text lines for the control.
   */
  @Input() rows: number = 2;
  /**
   * Determines textarea name attribute.
   */
  @Input() name: string = '';
  /**
   * Determines textarea readonly status.
   */
  @Input() readonly: boolean = false;
  /**
   * Sets required state for textarea.
   * This prop won't validate, it is just for accessibility reasons.
   */
  @Input() required: boolean = false;

  @Output() blur = new EventEmitter<Event>();

  onChange: (value: string) => void = noop;
  onTouch: () => void = noop;

  isDisabled = false;
  value = '';

  ngOnInit() {
    this.a11yCharactersCount.set(this.maxLength ?? 0);
  }

  handleOnTouch(event: Event) {
    this.onTouch();
    this.blur.emit(event);
  }

  handleOnInput(value: string) {
    this.onChange(value);
    this.updateA11yCharactersCount(value.length);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  focus(): void {
    this.textareaEl.nativeElement.focus();
  }

  writeValue(obj: string): void {
    this.value = obj;
  }
}
